import {
  useQuery,
  QueryClient,
  QueryClientProvider as QueryClientProviderBase,
} from "react-query";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";

// React Query client
const client = new QueryClient();

/**** USERS ****/

/**
 * Subscribe to user data via API.
 * Called automatically in `auth.js` — data is merged into `auth.user`.
 *
 * @param {string|false} uid - Firebase Auth UID, or falsy to skip
 * @param {object} authUser - Object with { id, accessToken } for auth headers
 */
export function useUser(uid, authUser) {
  return useQuery(
    ["user", { uid }],
    async () => {
      if (!authUser?.id || !authUser?.accessToken) return null;
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getUserMe,
          { id: authUser.id, accessToken: authUser.accessToken },
          null
        );
        return res.data || null;
      } catch (error) {
        // SOUND-715: authenticated with no user document yet — a signup waiting
        // on email confirmation. Reported as null ("no extra data") rather than
        // thrown, because auth.js turns a throw here into a raw error screen:
        // that is the blank page at /registration for a half-finished signup.
        if (
          error?.response?.status === 401 &&
          error?.response?.data?.error === "User not found"
        ) {
          return null;
        }
        throw error;
      }
    },
    {
      enabled: !!uid && !!authUser?.accessToken,
      staleTime: 60_000, // 1 minute
      refetchOnWindowFocus: false,
    }
  );
}

/**
 * Create a new user profile via API.
 * Called during signup in auth.js.
 */
/**
 * Fetch the CURRENT user's server-side record, or null if they have none.
 *
 * SOUND-715. Signup now leaves a Firebase account behind while the person
 * confirms their email, so "authenticated but not provisioned" is a real,
 * resumable state rather than a bug. Sign-in needs to tell it apart from a
 * genuinely broken session, and `useUser` cannot: it throws, and auth.js reads
 * a throw as a fatal error.
 *
 * A 401 here means the API could not resolve a user document for a token it
 * just accepted — `identifyUser` answers "User not found" that way. We report
 * it as null so the caller can offer to finish provisioning instead of
 * stranding someone on an error screen with no route forward.
 *
 * @returns {Promise<object|null>}
 */
export async function fetchCurrentUser(authUser) {
  if (!authUser?.id || !authUser?.accessToken) return null;
  try {
    const res = await makeApiRequest("get", apiRoutes.getUserMe, authUser, null);
    return res.data || null;
  } catch (error) {
    if (error?.response?.status === 401) return null;
    throw error;
  }
}

/**
 * Ask the API to send the branded confirmation email.
 *
 * SOUND-715. Replaces the Firebase client SDK's sendEmailVerification, which
 * delivers Firebase's own default — no logo, no branding, a bare action URL on
 * a grey page. The API mints the same action link with the Admin SDK and sends
 * it through templates/email-verification.ejs instead.
 *
 * Takes NO email address. The server reads it off the authenticated account, so
 * this cannot be pointed at somebody else.
 */
export async function sendVerificationEmail(authUser, turnstileToken) {
  const res = await makeApiRequest("post", apiRoutes.sendVerification, authUser, null, {
    turnstileToken,
  });
  return res.data;
}

export async function createUser(uid, data, authUser) {
  const res = await makeApiRequest(
    "post",
    apiRoutes.createUserProfile,
    { id: authUser?.id || uid, accessToken: authUser?.accessToken || "" },
    null,
    data
  );
  // Invalidate the useUser query so useMergeExtraData picks up the new profile immediately
  client.invalidateQueries(["user", { uid }]);
  return res;
}

/**
 * Update an existing user profile via API.
 */
export async function updateUser(uid, data, authUser) {
  const res = await makeApiRequest(
    "put",
    apiRoutes.updateUserProfile(uid),
    { id: authUser?.id || uid, accessToken: authUser?.accessToken || "" },
    null,
    data
  );
  // Refresh the merged auth user so firstName/lastName (etc.) reflect app-wide
  // immediately (nav avatar, headers) — mirrors createUser's invalidation.
  client.invalidateQueries(["user", { uid }]);
  return res;
}

/**** React Query context provider ****/
export function QueryClientProvider(props) {
  return (
    <QueryClientProviderBase client={client}>
      {props.children}
    </QueryClientProviderBase>
  );
}
